import { makeStyles, Tooltip } from '@material-ui/core';
import StarIcon from '@material-ui/icons/Star';
import { DIMENSION_CONFIG } from 'components/shared/experienceDimensions';
import { useExperienceSearchContext } from 'contexts/ExperienceSearchContext';
import usePermissions from 'hooks/usePermissions';

// Dimensions where the absolute-hours figure is hidden when the viewer lacks
// hours permission — mirrors ExperienceProfile's set.
const SENSITIVE_HOURS_DIMENSIONS = new Set(['country', 'partner', 'client', 'workType', 'practiceArea', 'industry', 'teamMember']);

const countryNames = new Intl.DisplayNames(['en'], { type: 'region' });
const getCountryName = (code: string) => { try { return countryNames.of(code) || code; } catch (e) { return code; } };

const useStyles = makeStyles({
  container: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: '5px 12px',
    marginTop: 4,
    userSelect: 'none',
    WebkitUserSelect: 'none',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '5px 0',
    '&:not(:last-child)': {
      borderBottom: '1px solid #f2f2f2',
    },
  },
  flag: {
    width: 14,
    height: 10,
    borderRadius: 1,
    boxShadow: '0 0 0 0.5px rgba(0,0,0,0.15)',
    flexShrink: 0,
  },
  avatar: {
    width: 16,
    height: 16,
    borderRadius: '50%',
    backgroundColor: '#eee',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 7,
    fontWeight: 700,
    color: '#888',
    flexShrink: 0,
    overflow: 'hidden',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover' as const,
    },
  },
  label: {
    fontSize: 12,
    color: '#444',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const,
    lineHeight: 1.3,
  },
  barTrack: {
    width: 50,
    height: 10,
    borderRadius: 3,
    backgroundColor: '#f0f0f0',
    overflow: 'hidden',
    flexShrink: 0,
  },
  barFill: {
    height: '100%',
    borderRadius: 3,
  },
  value: {
    fontSize: 10,
    color: '#999',
    minWidth: 28,
    textAlign: 'right' as const,
    flexShrink: 0,
  },
});

const formatHours = (minutes: number) => {
  if (!minutes) return '—';
  const hours = Math.round(minutes / 60);
  if (hours === 0) return '< 1h';
  if (hours >= 1000) return `${(hours / 1000).toFixed(1)}k`;
  return `${hours}h`;
};

const getInitials = (name: string) => {
  const parts = name.split(' ');
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
};

const SCOPE_LABELS: Record<string, string> = { allTime: 'all time', pastYear: 'the past year', pastMonth: 'the past 3 months' }; // pastMonth is the past 3 months
const MIN_BAR_MINUTES = 50 * 60;
const MAX_BAR_MINUTES = 1000 * 60;

const ExperienceAnnotation = ({ userId, userName }: { userId: string; userName?: string }) => {
  const classes = useStyles();
  const { experienceAnnotations, experienceScope } = useExperienceSearchContext();
  const { canViewHours } = usePermissions();

  const entries = experienceAnnotations?.[userId];
  if (!entries?.length) return null;

  const name = userName || 'This person';
  const scopeLabel = SCOPE_LABELS[experienceScope || 'allTime'] || 'all time';

  return (
    <div className={classes.container}>
      {entries.map(entry => {
        const config = DIMENSION_CONFIG[entry.dimension];
        const color = config?.color || '#888';
        const barMax = Math.min(Math.max(entry.maxMinutes || entry.totalMinutes, MIN_BAR_MINUTES), MAX_BAR_MINUTES);
        const hours = Math.round(entry.totalMinutes / 60);
        const hideHours = !canViewHours && SENSITIVE_HOURS_DIMENSIONS.has(entry.dimension);
        const matterSuffix = entry.matterCount === 1 ? 'matter' : 'matters';

        let tooltip = '';
        if (entry.totalMinutes === 0) {
          if (entry.dimension === 'country') tooltip = `${name} has no matters with clients from ${entry.label} over ${scopeLabel}`;
          else if (entry.dimension === 'partner') tooltip = `${name} has no matters led by ${entry.label} over ${scopeLabel}`;
          else tooltip = `${name} has no matters for ${entry.label} over ${scopeLabel}`;
        } else if (hideHours) {
          if (entry.dimension === 'country') tooltip = `${name} has ${entry.matterCount} ${matterSuffix} with clients from ${entry.label} over ${scopeLabel}`;
          else if (entry.dimension === 'partner') tooltip = `${name} has ${entry.matterCount} ${matterSuffix} led by ${entry.label} over ${scopeLabel}`;
          else tooltip = `${name} has ${entry.matterCount} ${matterSuffix} for ${entry.label} over ${scopeLabel}`;
        } else {
          if (entry.dimension === 'country') tooltip = `${name} has recorded ${hours} hours on matters with clients from ${entry.label} over ${scopeLabel}`;
          else if (entry.dimension === 'partner') tooltip = `${name} has recorded ${hours} hours over ${scopeLabel} on matters led by ${entry.label}`;
          else tooltip = `${name} has recorded ${hours} hours for ${entry.label} over ${scopeLabel}`;
        }

        return (
          <div key={`${entry.dimension}:${entry.key}`} className={classes.row}>
            {entry.starred && (
              <Tooltip title={`${name} has indicated a preference for ${entry.label}`} arrow placement="top">
                <StarIcon style={{ color: '#F5A623', fontSize: 18, flexShrink: 0, marginLeft: -2, marginRight: -2 }} />
              </Tooltip>
            )}
            {entry.dimension === 'country' && (
              <span className={`fi fi-${entry.key.toLowerCase()} ${classes.flag}`} />
            )}
            {entry.dimension === 'partner' && (
              <div className={classes.avatar}>
                {entry.profilePicture ? (
                  <img
                    alt={entry.label}
                    src={'https://d378wr66tg5qg7.cloudfront.net/' + entry.profilePicture}
                  />
                ) : getInitials(entry.label)}
              </div>
            )}
            <span className={classes.label}>{entry.label}</span>
            <Tooltip title={tooltip} arrow placement="top">
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <div className={classes.barTrack}>
                  {entry.totalMinutes > 0 && (
                    <div
                      className={classes.barFill}
                      style={{
                        width: `${Math.min(Math.max((entry.totalMinutes / barMax) * 100, 4), 100)}%`,
                        backgroundColor: color,
                        opacity: 0.6,
                      }}
                    />
                  )}
                </div>
                <span className={classes.value}>
                  {hideHours ? `${entry.matterCount} ${matterSuffix}` : formatHours(entry.totalMinutes)}
                </span>
              </div>
            </Tooltip>
          </div>
        );
      })}
    </div>
  );
};

export default ExperienceAnnotation;
